import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { createPlatformCheckout, getPlatformPaymentStatus } from 'src/api';
import { useBoolean } from 'src/hooks/use-boolean';
import { PlatformCheckoutSession, PlatformInvoice } from 'src/types';
import { isSuccessResponse } from 'src/utils/response-type-guards';
import {
  platformInvoiceKeys,
  platformPaymentStatusKeys,
  subscriptionStatusKeys,
} from 'src/utils/query-keys';

import { BillingCheckoutContextType, CheckoutStage } from './billing-checkout-context';

// ----------------------------------------------------------------------

/** How often we ask our own backend whether the provider settled the payment. */
const POLL_INTERVAL_MS = 4000;

/**
 * The provider page posts this when the shopper leaves the form. It only makes
 * us poll sooner — our backend stays the source of truth for the outcome.
 */
const CHECKOUT_FINISHED_MESSAGE = 'menta:checkout-finished';

const isCheckoutFinishedMessage = (data: unknown) =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === CHECKOUT_FINISHED_MESSAGE;

const getOrigin = (url: string) => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

// ----------------------------------------------------------------------

export const useCheckoutSession = (): BillingCheckoutContextType => {
  const queryClient = useQueryClient();

  const { value: isOpen, onTrue: openDialog, onFalse: closeDialog } = useBoolean();
  const [stage, setStage] = useState<CheckoutStage>('preparing');
  const [session, setSession] = useState<PlatformCheckoutSession | null>(null);
  const [failureReason, setFailureReason] = useState<string | null>(null);

  /** Kept so "Retry" can request a fresh session for the same invoice. */
  const invoiceIdRef = useRef<PlatformInvoice['id'] | undefined>(undefined);

  const paymentId = session?.payment_id ?? null;
  const isAwaitingResult = isOpen && (stage === 'checkout' || stage === 'confirming');

  const refreshBillingData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: subscriptionStatusKeys.root });
    queryClient.invalidateQueries({ queryKey: platformInvoiceKeys.root });
  }, [queryClient]);

  const { mutate: startSession } = useMutation({
    mutationFn: createPlatformCheckout,
    onMutate: () => {
      setStage('preparing');
      setSession(null);
      setFailureReason(null);
    },
    onSuccess: (response) => {
      if (!response || !isSuccessResponse(response)) {
        setFailureReason(response?.message ?? null);
        setStage('failed');
        return;
      }

      setSession(response);
      setStage('checkout');
    },
    onError: () => setStage('failed'),
  });

  const { data: payment, refetch: refetchPayment } = useQuery({
    queryKey: platformPaymentStatusKeys.detail(paymentId),
    queryFn: () => (paymentId ? getPlatformPaymentStatus(paymentId) : null),
    enabled: isAwaitingResult && !!paymentId,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    gcTime: 0,
  });

  // Settle the dialog as soon as the backend reports a final payment state.
  useEffect(() => {
    if (!isAwaitingResult) return;
    if (!payment || !isSuccessResponse(payment) || payment.state === 'pending') return;

    if (payment.state === 'succeeded') {
      setFailureReason(null);
      setStage('success');
      refreshBillingData();
      return;
    }

    setFailureReason(payment.failure_reason);
    refreshBillingData();
    setStage('failed');
  }, [payment, isAwaitingResult, refreshBillingData]);

  // The provider tells us it is done; verify immediately instead of waiting out the poll.
  useEffect(() => {
    if (!isAwaitingResult || !session) return undefined;

    const providerOrigin = getOrigin(session.checkout_url);

    const handleMessage = (event: MessageEvent) => {
      if (!providerOrigin || event.origin !== providerOrigin) return;
      if (!isCheckoutFinishedMessage(event.data)) return;

      setStage('confirming');
      refetchPayment();
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [isAwaitingResult, session, refetchPayment]);

  const openCheckout = useCallback(
    (invoiceId?: PlatformInvoice['id']) => {
      invoiceIdRef.current = invoiceId;
      openDialog();
      startSession({ invoice_id: invoiceId });
    },
    [startSession, openDialog]
  );

  const retryCheckout = useCallback(() => {
    startSession({ invoice_id: invoiceIdRef.current });
  }, [startSession]);

  const closeCheckout = useCallback(() => {
    // The shopper may have paid and closed before we saw it — re-read the truth.
    if (isAwaitingResult) refreshBillingData();

    closeDialog();
    setStage('preparing');
    setSession(null);
    setFailureReason(null);
  }, [isAwaitingResult, refreshBillingData, closeDialog]);

  return useMemo(
    () => ({
      isOpen,
      stage,
      session,
      failureReason,
      openCheckout,
      retryCheckout,
      closeCheckout,
    }),
    [isOpen, stage, session, failureReason, openCheckout, retryCheckout, closeCheckout]
  );
};
